import { ReactElement } from 'react';
import { IconButton } from '@upmc/ims.component-library';
import { debounce } from '../../../../utils/misc/debounce';
import Card, {
	Actions,
	ActionIcons,
} from '../../../../components/UI/MDC/Card/Card';
import Icon from '../../../../components/UI/MDC/Icon/Icon';
import {
	Headline5,
	Subtitle2,
} from '../../../../components/UI/MDC/Typography/Typography';
import { CSS_CLASSES } from './_constants';
import { propTypes } from './_types';

import classes from './Widget.module.scss';
import { nanoid } from 'nanoid';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useState } from 'react';
/**
 * Functional component for rendering content in "widget" style.
 *
 * @param {Object} props JSX attributes passed to component.
 * @param {Node|Node[]} props.children required JSX rendered as widget contents.
 * @param {string} props.className CSS applied to top component.
 * @param {string=} props.id nullable root element identifier.
 * @param {Function} props.onRefresh nullable callback to be executed on refresh action.
 * @param {string} props.subtitle nullable secondary widget text.
 * @param {string} props.title required primary widget text.
 * @returns {ReactElement} rendered component.
 */
const widget = ({
	children,
	className,
	id = null,
	onRefresh,
	subtitle,
	title,
	customActions,
	type,
}) => {
	const WidgetActions = () => {
		return (
			<>
				<Actions>
					<ActionIcons>
						<>
							{customActions && customActions()}
							{onRefresh && (
								<IconButton
									onClick={debounce(onRefresh)}
									id={`refresh_icon${nanoid()}`}
								>
									<Icon icon='refresh' />
								</IconButton>
							)}
						</>
					</ActionIcons>
				</Actions>
			</>
		);
	};

	const AccordionType = () => {
		const [expanded, setExpanded] = useState(true);
		const toggleExpanded = () => {
			setExpanded(!expanded);
		};

		return (
			<>
				<div className={className}>
					<Accordion expanded={expanded} onChange={toggleExpanded}>
						<AccordionSummary
							expandIcon={<ExpandMoreIcon />}
							aria-controls='panel1a-content'
							id='panel1a-header'
							className={classes[CSS_CLASSES.AccordionSummary]}
						>
							<div id={id}>
								<Headline5
									className={classes[CSS_CLASSES.TITLE]}
								>
									{title}
								</Headline5>
								<Subtitle2
									className={classes[CSS_CLASSES.SUBTITLE]}
								>
									{subtitle}
								</Subtitle2>
							</div>
						</AccordionSummary>
						<AccordionDetails>{children}</AccordionDetails>
						{onRefresh || customActions ? <WidgetActions /> : null}
					</Accordion>
				</div>
			</>
		);
	};

	switch (type) {
		case WidgetTypes.accordionOpen:
			return <AccordionType />;
		default:
			return (
				<Card medium className={className} id={id}>
					<Headline5 className={classes[CSS_CLASSES.TITLE]}>
						{title}
					</Headline5>
					<Subtitle2 className={classes[CSS_CLASSES.SUBTITLE]}>
						{subtitle}
					</Subtitle2>
					{children}
					{onRefresh || customActions ? <WidgetActions /> : null}
				</Card>
			);
	}
};

widget.propTypes = propTypes;

export { widget as Widget };

export const WidgetTypes = {
	accordionOpen: 'accordion-open',
};
